import React, { useState, useEffect } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Bug, Lightbulb, Sparkles, MessageSquare, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { question: "Frage", bug: "Bug", feature: "Wunsch" };
const TYPE_ICON = { question: Sparkles, bug: Bug, feature: Lightbulb };
const STATUS_LABEL = {
    open: { label: "Offen", color: "bg-amber-100 text-amber-800" },
    in_progress: { label: "In Arbeit", color: "bg-blue-100 text-blue-800" },
    resolved: { label: "Gelöst", color: "bg-emerald-100 text-emerald-800" },
    closed: { label: "Geschlossen", color: "bg-neutral-100 text-neutral-600" },
};

export default function CasesPage() {
    const { user } = useAuth();
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [response, setResponse] = useState("");
    const [updating, setUpdating] = useState(false);

    const isAdmin = user?.role === "admin" || user?.role === "master";

    const fetchCases = async () => {
        setLoading(true);
        try {
            const params = filter !== "all" ? { status: filter } : {};
            const { data } = await api.get("/cases", { params });
            setCases(data.cases || []);
        } catch (e) {
            toast.error("Cases konnten nicht geladen werden");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCases(); }, [filter]);

    const updateCase = async (id, body) => {
        setUpdating(true);
        try {
            const { data } = await api.patch(`/cases/${id}`, body);
            toast.success("Case aktualisiert");
            setSelected(data);
            fetchCases();
        } catch (e) {
            toast.error("Update fehlgeschlagen");
        } finally {
            setUpdating(false);
        }
    };

    if (selected) {
        const TIcon = TYPE_ICON[selected.type] || Sparkles;
        return (
            <div className="space-y-5 max-w-3xl" data-testid="case-detail">
                <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setResponse(""); }} data-testid="case-detail-back">
                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Zurück
                </Button>
                <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
                                <TIcon className="w-3.5 h-3.5" />
                                {TYPE_LABEL[selected.type] || selected.type}
                                · {new Date(selected.created_at).toLocaleString("de-DE")}
                            </div>
                            <h1 className="text-2xl font-bold">{selected.subject}</h1>
                            <div className="text-sm text-neutral-500 mt-1">
                                von <strong>{selected.user_name || selected.user_email}</strong>
                            </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_LABEL[selected.status]?.color}`}>
                            {STATUS_LABEL[selected.status]?.label || selected.status}
                        </span>
                    </div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed bg-neutral-50 rounded-md p-4">
                        {selected.description}
                    </div>
                    {selected.related_question && (
                        <div className="text-xs text-neutral-500 border-l-2 border-neutral-300 pl-3">
                            Verbunden mit Frage: „{selected.related_question}“
                        </div>
                    )}

                    {selected.admin_response && (
                        <div className="border-t border-neutral-200 pt-4">
                            <Label className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Antwort vom Team</Label>
                            <div className="text-sm whitespace-pre-wrap bg-emerald-50 border border-emerald-200 rounded-md p-3">
                                {selected.admin_response}
                            </div>
                        </div>
                    )}

                    {isAdmin && (
                        <div className="border-t border-neutral-200 pt-4 space-y-3">
                            <Textarea
                                rows={4}
                                placeholder="Antwort an den Nutzer ..."
                                value={response}
                                onChange={(e) => setResponse(e.target.value)}
                                data-testid="case-admin-response"
                            />
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    size="sm"
                                    onClick={() => updateCase(selected.id, { admin_response: response || selected.admin_response, status: "in_progress" })}
                                    disabled={updating}
                                    variant="outline"
                                    data-testid="case-mark-in-progress"
                                >In Arbeit nehmen</Button>
                                <Button
                                    size="sm"
                                    onClick={() => updateCase(selected.id, { admin_response: response || selected.admin_response, status: "resolved" })}
                                    disabled={updating}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid="case-mark-resolved"
                                >Als gelöst markieren</Button>
                                <Button
                                    size="sm"
                                    onClick={() => updateCase(selected.id, { status: "closed" })}
                                    disabled={updating}
                                    variant="outline"
                                    data-testid="case-mark-closed"
                                >Schließen</Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="cases-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Cases & Support</h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        {isAdmin ? "Alle Cases aus dem Hilfe-System." : "Deine eingereichten Cases."}
                    </p>
                </div>
                <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden text-sm">
                    {[
                        { v: "all", l: "Alle" },
                        { v: "open", l: "Offen" },
                        { v: "in_progress", l: "In Arbeit" },
                        { v: "resolved", l: "Gelöst" },
                        { v: "closed", l: "Geschl." },
                    ].map(({ v, l }) => (
                        <button
                            key={v}
                            onClick={() => setFilter(v)}
                            className={`px-3 py-1.5 ${filter === v ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"} ${v !== "all" ? "border-l border-neutral-200" : ""}`}
                            data-testid={`case-filter-${v}`}
                        >
                            {l}
                        </button>
                    ))}
                </div>
            </header>

            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                {loading && <div className="p-8 text-center text-sm text-neutral-500">Lade Cases ...</div>}
                {!loading && cases.length === 0 && (
                    <div className="p-12 text-center" data-testid="cases-empty">
                        <MessageSquare className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
                        <div className="text-sm text-neutral-500">Keine Cases im aktuellen Filter.</div>
                    </div>
                )}
                {!loading && cases.length > 0 && (
                    <ul className="divide-y divide-neutral-100" data-testid="cases-list">
                        {cases.map((c) => {
                            const TIcon = TYPE_ICON[c.type] || Sparkles;
                            return (
                                <li key={c.id}>
                                    <button
                                        className="w-full text-left px-5 py-4 hover:bg-neutral-50 flex items-start gap-3"
                                        onClick={() => setSelected(c)}
                                        data-testid={`case-row-${c.id}`}
                                    >
                                        <TIcon className="w-4 h-4 mt-0.5 text-neutral-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className="font-medium truncate">{c.subject}</div>
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_LABEL[c.status]?.color}`}>
                                                    {STATUS_LABEL[c.status]?.label || c.status}
                                                </span>
                                            </div>
                                            <div className="text-xs text-neutral-500 mt-0.5 truncate">
                                                {c.user_name || c.user_email} · {new Date(c.created_at).toLocaleString("de-DE")}
                                            </div>
                                            <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{c.description}</div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

// Small label helper since shadcn's Label has issues being imported here without form
function Label({ className = "", children, ...props }) {
    return <label className={`text-sm font-medium ${className}`} {...props}>{children}</label>;
}
